/**
 * Содержимое карточки землетрясения: всё, что о событии известно.
 *
 * Строки собираются здесь, а не в компоненте, ровно по той же причине, что у
 * читалки косяка: «что показать» — логика и проверяется тестом, «как это
 * звучит» — каталог перевода. Числа и даты идут через `Intl`, иначе в русском
 * кабинете вылезла бы десятичная точка.
 *
 * Ничего не выдумываем: поля, которых у события нет (USGS не всегда отдаёт
 * глубину и описание места), в карточку просто не попадают. Пустая строка с
 * прочерком выглядела бы как «глубина ноль».
 */
import { formatDayTime, formatNumber, t, type Lang } from '../i18n'

export interface EarthquakeProps {
  id?: string | null
  magnitude?: number | null
  depth_km?: number | null
  time?: string | null
  place?: string | null
  event_type?: string | null
  status?: string | null
  usgs_url?: string | null
  impact_radius_km?: number | null
  recovery_hours?: number | null
  [key: string]: unknown
}

export interface EarthquakeRow {
  labelKey: string
  value: string
}

export interface EarthquakeCardView {
  /** Заголовок: магнитуда — единственное, что читают первым. */
  title: string
  /** Место словами от USGS; своего перевода у него нет и быть не может. */
  place: string | null
  rows: EarthquakeRow[]
  /** Действует ли ещё: событие старше срока восстановления влияния не имеет. */
  active: boolean | null
  url: string | null
}

/** Число или null. `Number(null)` даёт 0 — «поля нет» превратилось бы в «ноль». */
function num(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

/** Часов между событием и «сейчас»; без времени события — null. */
export function hoursSince(iso: string | null | undefined, now: Date): number | null {
  if (!iso) return null
  const then = new Date(iso)
  if (Number.isNaN(then.getTime())) return null
  return (now.getTime() - then.getTime()) / 3_600_000
}

export function earthquakeCardView(
  props: EarthquakeProps,
  lang: Lang,
  now: Date = new Date(),
): EarthquakeCardView {
  const magnitude = num(props.magnitude)
  const depth = num(props.depth_km)
  const radius = num(props.impact_radius_km)
  const recovery = num(props.recovery_hours)
  const elapsed = hoursSince(props.time ?? null, now)

  const rows: EarthquakeRow[] = []

  if (props.time) {
    // Время события — UTC: так его отдаёт USGS и так оно лежит в базе. Пояс
    // судна тут не при чём — событие одно на весь океан.
    rows.push({ labelKey: 'models.quake.when', value: formatDayTime(lang, props.time) })
  }
  if (elapsed != null && elapsed >= 0) {
    rows.push({
      labelKey: 'models.quake.ago',
      value: t('models.quake.hours', { count: Math.round(elapsed) }),
    })
  }
  // Единица — отдельным ключом и приклеивается пробелом, как везде в кабинете:
  // «км» в самой фразе завело бы вторую копию единицы измерения в каталоге.
  if (depth != null) {
    // Округляем сами и печатаем без фиксированной разрядности: `digits: 1`
    // дало бы «33,0 км» там, где USGS сообщил ровно 33.
    rows.push({
      labelKey: 'models.quake.depth',
      value: `${formatNumber(lang, Math.round(depth * 10) / 10)} ${t('units.km')}`,
    })
  }
  if (radius != null) {
    rows.push({
      labelKey: 'models.quake.radius',
      value: `${formatNumber(lang, radius, 0)} ${t('units.km')}`,
    })
  }
  if (recovery != null) {
    rows.push({
      labelKey: 'models.quake.recovery',
      value: t('models.quake.hours', { count: recovery }),
    })
  }
  // Тип и состояние карточки USGS: «earthquake» против «quarry blast» — не
  // мелочь, взрыв в карьере рыбу не пугает так же, как толчок под водой.
  if (props.event_type) {
    rows.push({ labelKey: 'models.quake.type', value: String(props.event_type) })
  }
  if (props.status) {
    rows.push({ labelKey: 'models.quake.status', value: String(props.status) })
  }

  return {
    title: magnitude != null
      ? t('models.quake.titleM', { magnitude: formatNumber(lang, magnitude, 1) })
      : t('models.quake.title'),
    place: props.place?.trim() ? String(props.place).trim() : null,
    rows,
    active: elapsed == null || recovery == null ? null : elapsed >= 0 && elapsed < recovery,
    url: props.usgs_url?.trim() ? String(props.usgs_url).trim() : null,
  }
}
